import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  TextField,
} from "@mui/material";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import {
  UNABLE_TO_ASSESS_LABEL,
  VITAL_ROWS,
} from "../readmission-f2f-vitals-constant";
import { VitalsValue } from "../readmission-f2f-types";
import {
  calculateBmi,
  getVitalError,
  sanitizeVitalInput,
} from "../readmission-f2f-utils";

export interface VitalsGridProps {
  value: VitalsValue;
  onChange: (value: VitalsValue) => void;
}

const VitalsGrid = ({ value, onChange }: VitalsGridProps) => {
  const rowOf = (key: string) =>
    value[key] ?? { value: "", unable: false, note: "" };

  const update = (key: string, patch: Partial<VitalsValue[string]>) =>
    onChange({ ...value, [key]: { ...rowOf(key), ...patch } });

  const bmi = calculateBmi(rowOf("height").value, rowOf("weight").value);

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {VITAL_ROWS.map((row) => {
        const current = rowOf(row.key);
        const error = current.unable ? "" : getVitalError(row, current.value);
        return (
          <Flex key={row.key} gap={1.5} alignItems="center" flexWrap="wrap">
            <Text fontWeight={600} sx={{ minWidth: 130 }}>
              {row.label}
            </Text>
            <TextField
              size="small"
              sx={{ width: 160 }}
              disabled={current.unable}
              placeholder={row.placeholder}
              value={current.value}
              error={!!error}
              helperText={error}
              inputProps={{
                maxLength: row.maxLength,
                inputMode:
                  row.format === "bloodPressure"
                    ? "text"
                    : row.decimals
                      ? "decimal"
                      : "numeric",
              }}
              onChange={(e) =>
                update(row.key, {
                  value: sanitizeVitalInput(row, e.target.value),
                })
              }
            />
            <Text size={13} c="text.secondary" sx={{ minWidth: 48 }}>
              {row.unit}
            </Text>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={current.unable}
                  onChange={(e) =>
                    update(row.key, {
                      unable: e.target.checked,
                      value: e.target.checked ? "" : current.value,
                    })
                  }
                />
              }
              label={<Text size={13}>{UNABLE_TO_ASSESS_LABEL}</Text>}
            />
            {current.unable && (
              <TextField
                size="small"
                placeholder="Reason or note (optional)"
                value={current.note}
                onChange={(e) => update(row.key, { note: e.target.value })}
              />
            )}
          </Flex>
        );
      })}
      <Divider />
      <Flex gap={1.5} alignItems="center">
        <Text fontWeight={600} sx={{ minWidth: 130 }}>
          BMI
        </Text>
        <Text sx={{ minWidth: 160 }}>{bmi}</Text>
        <Text size={13} c="text.secondary">
          kg/m²
        </Text>
        <Text size={13} c="text.secondary">
          Calculated from height and weight
        </Text>
      </Flex>
    </Box>
  );
};

export default VitalsGrid;
